import { createElement, forwardRef } from "react";
import {
  AlertTriangle,
  Store,
  Gem,
  Home,
  Users,
  GitMerge,
  Package,
  ShoppingCart,
  Activity,
  Gauge,
  FileText,
  FileSearch,
  Settings,
  PoundSterling,
  LayoutDashboard,
  Plug,
  Camera,
  ShieldCheck,
  History,
  Printer,
  Boxes,
  Mail,
  Share2,
  Truck,
  Receipt,
  Building2,
  Handshake,
  KeyRound,
  ListOrdered,
  // Extras for the picker.
  Bell,
  Bookmark,
  Briefcase,
  Calculator,
  Calendar,
  BarChart3,
  ChartBar,
  ChartLine,
  ChartPie,
  CheckCircle,
  Clipboard,
  Clock,
  Cloud,
  Coins,
  CreditCard,
  Database,
  Download,
  Edit,
  ExternalLink,
  Eye,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  HelpCircle,
  Image,
  Info,
  Layers,
  Link as LinkIcon,
  Lock,
  MapPin,
  MessageSquare,
  Newspaper,
  Notebook,
  Package2,
  Palette,
  Phone,
  PieChart,
  Search,
  Send,
  Star,
  Tag,
  Target,
  TrendingUp,
  Trophy,
  Upload,
  User,
  Wallet,
  Wrench,
  Zap,
  Recycle,
  FlaskConical,
  ShoppingBag,
  FolderTree,
  LayoutGrid,
  Box,
  UsersRound,
  Tags,
  Paintbrush,
  Megaphone,
  Sparkles,
  Plus,
  RadioTower,
  SlidersHorizontal,
  UserCog,
  UserCircle,
  CalendarClock,
  ClipboardList,
  LayoutTemplate,
  RotateCcw,
  HardDrive,
  Scissors,
  Scale,
  ArrowLeftRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Landmark,
  type LucideIcon,
  type LucideProps,
} from "lucide-react";

export const WhatsAppBrandIcon: LucideIcon = forwardRef<SVGSVGElement, LucideProps>(
  ({ size = 24, className, color = "currentColor", ...props }, ref) =>
    createElement(
      "svg",
      {
        ...props,
        ref,
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: color,
        className,
        "aria-hidden": true,
      },
      createElement("path", {
        d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z",
      }),
    ),
);

/**
 * Curated registry of icon components exposed to the Sidebar Item picker.
 * The string key is what we persist in `sidebar_items.icon`. When rendering
 * we look up the component via `getIcon(name)`; unknown names fall back to
 * `Home` so the sidebar never crashes on a stale icon string.
 *
 * Keep the original 23 nav icons at the top so they always show first in the
 * picker grid; everything else is a useful general-purpose extra. WhatsApp
 * keeps its official brand mark here.
 */
export const SIDEBAR_ICONS: Record<string, LucideIcon> = {
  AlertTriangle,
  Store,
  Gem,
  Home,
  Users,
  GitMerge,
  Package,
  ShoppingCart,
  Activity,
  Gauge,
  FileText,
  FileSearch,
  Settings,
  PoundSterling,
  LayoutDashboard,
  Plug,
  Camera,
  ShieldCheck,
  History,
  Printer,
  Boxes,
  Mail,
  Share2,
  Truck,
  Receipt,
  Building2,
  Handshake,
  KeyRound,
  ListOrdered,
  Bell,
  Bookmark,
  Briefcase,
  Calculator,
  Calendar,
  BarChart3,
  ChartBar,
  ChartLine,
  ChartPie,
  CheckCircle,
  Clipboard,
  Clock,
  Cloud,
  Coins,
  CreditCard,
  Database,
  Download,
  Edit,
  ExternalLink,
  Eye,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  HelpCircle,
  Image,
  Info,
  Layers,
  Link: LinkIcon,
  Lock,
  MapPin,
  MessageSquare,
  Newspaper,
  Notebook,
  Package2,
  Palette,
  Phone,
  PieChart,
  Search,
  Send,
  Star,
  Tag,
  Target,
  TrendingUp,
  Trophy,
  Upload,
  User,
  Wallet,
  Wrench,
  Zap,
  Recycle,
  FlaskConical,
  ShoppingBag,
  FolderTree,
  LayoutGrid,
  Box,
  UsersRound,
  Tags,
  Paintbrush,
  Megaphone,
  Sparkles,
  Plus,
  RadioTower,
  SlidersHorizontal,
  UserCog,
  UserCircle,
  CalendarClock,
  ClipboardList,
  LayoutTemplate,
  RotateCcw,
  HardDrive,
  Scissors,
  Scale,
  ArrowLeftRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Landmark,
  WhatsApp: WhatsAppBrandIcon,
};

export const SIDEBAR_ICON_NAMES: readonly string[] = Object.keys(SIDEBAR_ICONS);

/** Look up an icon component by name. Falls back to `Home` for unknown names. */
export function getIcon(name: string | null | undefined): LucideIcon {
  if (!name) return Home;
  return SIDEBAR_ICONS[name] ?? Home;
}
